import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Modal, ModalTitle, ModalBody } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import EditIcon from 'design-system/src/assets/icons/edit.svg?react';
import { Input } from '../Input';
import { FormLabel } from '../FormLabel';
import { DropdownItem, DropdownWithPlaceholder } from '../Dropdown';
import { AddressAutocomplete, ParsedAddress, useGeocode } from '../AddressAutocomplete';
import { client, type SiteOfCareType, SITE_OF_CARE_TYPE_LABELS } from '../../api';
import { useCreateSiteOfCare } from '../../hooks/useBenefitPreferences';

const ADD_NEW_LOCATION = '__add_new__';

type FormData = {
  office_address: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zipcode: string;
  tin: string;
  site_type: SiteOfCareType | '';
};

type AddSiteOfCareModalProps = {
  open: boolean;
  onClose: () => void;
  /** Called with the new site's ID after successful creation */
  onSiteCreated?: (siteId: string) => void;
};

type OfficeDropdownProps = {
  offices: { address: string }[];
  isGeocoding: boolean;
  addressError: string | null;
  onSelect: (value: string) => void;
};

const OfficeDropdown = ({ offices, isGeocoding, addressError, onSelect }: OfficeDropdownProps) => (
  <div className="flex flex-col">
    <FormLabel htmlFor="office-select">Select address</FormLabel>
    <DropdownWithPlaceholder
      id="office-select"
      placeholder="Select an address"
      value=""
      onChange={(e) => onSelect(e.target.value as string)}
      disabled={isGeocoding}
    >
      <DropdownItem value={ADD_NEW_LOCATION} sx={{ color: 'var(--purple-700)' }}>
        + Add new location
      </DropdownItem>
      {offices.map((office) => (
        <DropdownItem key={office.address} value={office.address}>
          {office.address}
        </DropdownItem>
      ))}
    </DropdownWithPlaceholder>
    {addressError && <ErrorMessage>{addressError}</ErrorMessage>}
  </div>
);

type AddressSearchProps = {
  value: string;
  addressError: string | null;
  onChange: (val: string) => void;
  onAddressSelect: (address: ParsedAddress) => void;
  onManualEntry: () => void;
};

const AddressSearch = ({ value, addressError, onChange, onAddressSelect, onManualEntry }: AddressSearchProps) => (
  <div className="flex flex-col">
    <div className="mb-1 flex w-full items-center justify-between [&_label]:mb-0">
      <FormLabel htmlFor="address-input">Search address</FormLabel>
      <button
        type="button"
        onClick={onManualEntry}
        className="flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 hover:opacity-80"
      >
        <EditIcon width={14} height={14} className="text-purple-700" />
        <Typography styledAs="bodyExtraSmallSpaceMedium" color="var(--purple-700)">
          Enter address manually
        </Typography>
      </button>
    </div>
    <AddressAutocomplete
      label="Search address"
      hideLabel
      placeholder="Search for an address"
      value={value}
      onChange={onChange}
      onAddressSelect={onAddressSelect}
    />
    {addressError && <ErrorMessage>{addressError}</ErrorMessage>}
  </div>
);

type DetailsFormProps = {
  control: ReturnType<typeof useForm<FormData>>['control'];
  errors: ReturnType<typeof useForm<FormData>>['formState']['errors'];
};

const formatTin = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}-${digits.slice(2)}`;
};

const DetailsForm = ({ control, errors }: DetailsFormProps) => (
  <>
    <Controller
      control={control}
      name="address_line_1"
      rules={{ required: 'Address is required' }}
      render={({ field }) => (
        <Input label="Address line 1" placeholder="Street address" hasError={!!errors.address_line_1} {...field} />
      )}
    />
    {errors.address_line_1 && <ErrorMessage>{errors.address_line_1.message}</ErrorMessage>}

    <Controller
      control={control}
      name="address_line_2"
      render={({ field }) => (
        <Input label="Address line 2 (optional)" placeholder="Apt, suite, unit, etc." {...field} />
      )}
    />

    <div className="flex gap-4">
      <div className="flex-1">
        <Controller
          control={control}
          name="city"
          rules={{ required: 'City is required' }}
          render={({ field }) => <Input label="City" placeholder="City" hasError={!!errors.city} {...field} />}
        />
        {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
      </div>
      <div className="flex-1">
        <Controller
          control={control}
          name="state"
          rules={{ required: 'State is required' }}
          render={({ field }) => <Input label="State" placeholder="State" hasError={!!errors.state} {...field} />}
        />
        {errors.state && <ErrorMessage>{errors.state.message}</ErrorMessage>}
      </div>
    </div>

    <div className="flex gap-4">
      <div className="flex-1">
        <Controller
          control={control}
          name="zipcode"
          rules={{ required: 'ZIP code is required' }}
          render={({ field }) => (
            <Input label="ZIP code" placeholder="ZIP code" hasError={!!errors.zipcode} {...field} />
          )}
        />
        {errors.zipcode && <ErrorMessage>{errors.zipcode.message}</ErrorMessage>}
      </div>
      <div className="flex-1">
        <Controller
          control={control}
          name="tin"
          rules={{ required: 'TIN is required' }}
          render={({ field }) => (
            <Input
              label="TIN"
              placeholder="00-0000000"
              hasError={!!errors.tin}
              {...field}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(formatTin(e.target.value))}
            />
          )}
        />
        {errors.tin && <ErrorMessage>{errors.tin.message}</ErrorMessage>}
      </div>
    </div>

    <div className="flex flex-col">
      <FormLabel htmlFor="site-type">Site type</FormLabel>
      <Controller
        control={control}
        name="site_type"
        rules={{ required: 'Site type is required' }}
        render={({ field }) => (
          <DropdownWithPlaceholder
            id="site-type"
            placeholder="Select site type"
            value={field.value}
            onChange={field.onChange}
            $hasError={!!errors.site_type}
          >
            {(Object.entries(SITE_OF_CARE_TYPE_LABELS) as [SiteOfCareType, string][]).map(([value, label]) => (
              <DropdownItem key={value} value={value}>
                {label}
              </DropdownItem>
            ))}
          </DropdownWithPlaceholder>
        )}
      />
      {errors.site_type && <ErrorMessage>{errors.site_type.message}</ErrorMessage>}
    </div>
  </>
);

const AddSiteOfCareModal: React.FC<AddSiteOfCareModalProps> = ({ open, onClose, onSiteCreated }) => {
  const [step, setStep] = useState<'select' | 'details'>('select');
  const [showAddressInput, setShowAddressInput] = useState(false);
  const [addressInputValue, setAddressInputValue] = useState('');
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const { geocode } = useGeocode();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      office_address: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zipcode: '',
      tin: '',
      site_type: '',
    },
  });

  const officesQuery = useQuery<{ offices: { address: string }[] }>({
    queryKey: ['prescription-offices'],
    queryFn: () => client.get('/settings/prescription-offices').then((res) => res.data),
    enabled: open,
  });

  const createSiteOfCare = useCreateSiteOfCare({
    onSuccess: (site) => {
      toast.custom(() => <Toast variant="success">Site of care added</Toast>);
      onSiteCreated?.(site.id);
      handleClose();
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const offices = officesQuery.data?.offices ?? [];

  const handleClose = () => {
    reset();
    setStep('select');
    setShowAddressInput(false);
    setAddressInputValue('');
    setIsGeocoding(false);
    setAddressError(null);
    onClose();
  };

  const setAddressFields = (address: ParsedAddress) => {
    setValue('office_address', address.formatted_address);
    setValue('address_line_1', address.address_line_1);
    setValue('address_line_2', address.address_line_2);
    setValue('city', address.city);
    setValue('state', address.state);
    setValue('zipcode', address.zipcode);
  };

  const handleDropdownChange = async (value: string) => {
    if (value === ADD_NEW_LOCATION) {
      setShowAddressInput(true);
      return;
    }

    // Selected an existing office - geocode it
    setIsGeocoding(true);
    setAddressError(null);
    try {
      const parsed = await geocode(value);
      if (parsed) {
        setAddressFields(parsed);
        setStep('details');
      } else {
        setAddressError('Could not find address. Please try adding a new location.');
      }
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleAddressSelect = (address: ParsedAddress) => {
    setAddressFields(address);
    setAddressInputValue(address.formatted_address);
    setAddressError(null);
    setStep('details');
  };

  const handleBack = () => {
    setStep('select');
    setShowAddressInput(false);
    setAddressInputValue('');
    setAddressError(null);
  };

  const onSubmit = (data: FormData) => {
    // Compose office_address from manual entry fields if not set (e.g., manual entry flow)
    const officeAddress = data.office_address || `${data.address_line_1}, ${data.city}, ${data.state} ${data.zipcode}`;

    createSiteOfCare.mutate({
      office_address: officeAddress,
      tin: data.tin,
      site_type: data.site_type as SiteOfCareType,
      address_line_1: data.address_line_1,
      address_line_2: data.address_line_2 || null,
      city: data.city,
      state: data.state,
      zipcode: data.zipcode,
    });
  };

  const isDetailsValid =
    watch('address_line_1') &&
    watch('city') &&
    watch('state') &&
    watch('zipcode') &&
    watch('site_type') &&
    watch('tin');

  const footerButtonConfig =
    step === 'details'
      ? {
          showCancelButton: true,
          cancelButtonLabel: 'Back',
          onCancelClick: handleBack,
          submitButton: {
            label: 'Save',
            disabled: !isDetailsValid,
            isLoading: createSiteOfCare.isPending,
            onClick: handleSubmit(onSubmit),
          },
        }
      : showAddressInput
        ? {
            showCancelButton: true,
            cancelButtonLabel: 'Back',
            onCancelClick: () => setShowAddressInput(false),
          }
        : { showCancelButton: true };

  const selectStepContent = officesQuery.isPending ? (
    <LoadingSpinner height="100px" />
  ) : !showAddressInput && offices.length > 0 ? (
    <OfficeDropdown
      offices={offices}
      isGeocoding={isGeocoding}
      addressError={addressError}
      onSelect={handleDropdownChange}
    />
  ) : (
    <AddressSearch
      value={addressInputValue}
      addressError={addressError}
      onChange={(val) => {
        setAddressInputValue(val);
        setAddressError(null);
      }}
      onAddressSelect={handleAddressSelect}
      onManualEntry={() => setStep('details')}
    />
  );

  const modalBodyContent = step === 'select' ? selectStepContent : <DetailsForm control={control} errors={errors} />;

  return (
    <Modal open={open} onClose={handleClose} maxWidth="640px" footerButtonConfig={footerButtonConfig}>
      <ModalTitle>Add site of care</ModalTitle>
      <ModalBody>
        <div className="flex flex-col gap-4">{modalBodyContent}</div>
      </ModalBody>
    </Modal>
  );
};

export default AddSiteOfCareModal;
